import {
  CompleteFinchConnectionMutationVariables,
  OrganizationDetailFragment,
  useAnalytics,
  useCompleteFinchConnectionMutation,
} from '@alice-financial/alice-sdk'
import { datadogRum } from '@datadog/browser-rum'
import { useFinchConnect } from '@tryfinch/react-connect'
import { useState } from 'react'

const FINCH_CLIENT_ID = import.meta.env.PUBLIC_FINCH_CLIENT_ID

type UseFinchIframeParams = {
  onSuccessConnectingPayrollAccount: () => void
  onErrorConnectingPayrollAccount: () => void
  onCloseConnectingPayrollAccount: () => void
}

export type FinchConnectReturnParam = 'error_occurred' | 'iframe_closed' | 'completed'

/**
 * Initializes and opens the Finch connect iFrame.
 *
 * Requires:
 * - session ID: Backend-generated identifier for the user's Finch connect interaction
 * - Finch connection ID: Primary key of our DB record storing Finch connection data,
 *   including the linked Alice organization and Finch-specific connection details
 *
 * After user authentication, automatically sends the authorization code to the backend
 * to exchange for an access token and complete the Finch connection.
 */
export const useFinchIframe = ({
  onSuccessConnectingPayrollAccount,
  onErrorConnectingPayrollAccount,
  onCloseConnectingPayrollAccount,
}: UseFinchIframeParams) => {
  const analytics = useAnalytics()

  if (!FINCH_CLIENT_ID) {
    throw new Error('FinchAuthGateway: process.env.FINCH_CLIENT_ID not defined')
  }

  // State to track the Finch connection ID as soon as it's available
  const [finchConnectionId, setFinchConnectionId] = useState<number | null>(null)

  const { mutate: exchangeAuthorizationTokenMutation, isLoading: isTokenExchangeLoading } =
    useCompleteFinchConnectionMutation({
      onSuccess: (_data) => {
        analytics.track('finch_connect.success.access_token_generation')
        onSuccessConnectingPayrollAccount()
      },
      onError: (error) => {
        analytics.track('finch_connect.error.access_token_generation', { errorMessage: error.message })
        datadogRum.addError('Finch Connect Error (access token generation)', {
          message: error.message,
          source: 'finch-iframe',
        })
        onErrorConnectingPayrollAccount()
      },
    })

  const onSuccessFinchIframe = ({ code: authorizationCode }: { code: string }) => {
    if (!finchConnectionId) {
      throw new Error('FinchAuthGateway: finchConnectionId is required')
    }

    const mutationVariables: CompleteFinchConnectionMutationVariables = {
      authorizationCode,
      finchConnectionId,
    }

    analytics.track('finch_connect.success.authorization_code_generation')
    exchangeAuthorizationTokenMutation(mutationVariables)
    // Clear the connection ID after successful use
    setFinchConnectionId(null)
  }

  const onErrorFinchIframe = ({ errorMessage }: { errorMessage: string }) => {
    // The error UI is handled by the Finch iFrame, so we only need to track the error, not display it
    analytics.track('finch_connect.error.authorization_code_generation', { errorMessage })
    datadogRum.addError('Finch Connect Error (authorization code generation)', {
      message: errorMessage,
      source: 'finch-iframe',
    })
  }

  const onCloseFinchIframe = () => {
    analytics.track('finch_connect.iframe_closed')
    onCloseConnectingPayrollAccount()
  }

  // Parameters like `products, sandbox, payroll_provider` are now specified at the
  // backend when generating the Finch session.
  const { open: finchOpen } = useFinchConnect({
    clientId: FINCH_CLIENT_ID,
    onSuccess: onSuccessFinchIframe,
    onError: onErrorFinchIframe,
    onClose: onCloseFinchIframe,
  })

  const openFinchIframe = (finchSessionId: string, finchConnectionId: number) => {
    if (!finchSessionId) {
      throw new Error('FinchAuthGateway: finchSessionId is required')
    }

    analytics.track('finch_connect.iframe_opened')
    // Store finchConnectionId (is needed after the user has authenticated and the
    // authorization code is exchanged for an access token)
    setFinchConnectionId(finchConnectionId)
    finchOpen({ sessionId: finchSessionId })
  }

  return { isTokenExchangeLoading, openFinchIframe }
}

/**
 * This hook checks if a given payroll platform is enabled for Finch for the admins onboarding
 * by comparing it against the PLATFORMS_ENABLED_FOR_FINCH_ONBOARDING environment variable.
 * Returns true if the platform is in the enabled list, false otherwise.
 */
export const useIsFinchOnboardingEnabledForPlatform = (
  org: OrganizationDetailFragment | null | undefined
): boolean => {
  return Boolean(org?.payrollConnection?.isFinchConnectEnabled)
}
